import config from '@haaretz/l-config';
import isStorybook from '@haaretz/s-common-utils/isStorybook';
import timeoutSignalFactory from '@haaretz/s-common-utils/timeoutSignal';
import { hostname as hostnameWithoutSubdomain } from '@haaretz/s-consts';
import getOperationName from '@haaretz/s-graphql-utils/getOperationName';
import getOperationType from '@haaretz/s-graphql-utils/getOperationType';
import parseQuery from '@haaretz/s-graphql-utils/parseQuery';
import switchToDomain from '@haaretz/s-url-utils/switchToDomain';
import hexEnc from 'crypto-js/enc-hex';
import sha256 from 'crypto-js/sha256';

import { GraphqlFetchError, logQueryFailure } from './queryFailure';

import type { Env, HtzWebsiteExternalURL } from '@haaretz/s-types';
import type { QueryFunctionContext } from '@tanstack/react-query';

const gqlServerKey = typeof window === 'undefined' ? 'gqlServerCluster' : 'gqlServer';

const gqlServerUrl = config.get(gqlServerKey);
const gqlServerClusterUrl = config.has('cmsGqlCluster') ? config.get('cmsGqlCluster') : null;

const praedictaUrl = config.has('praedictaUri') ? config.get('praedictaUri') : null;

const userInfoUrl = config.has('userInfo') ? config.get('userInfo') : null;

const cmsGqlUrl = config.has('cmsGql') ? config.get('cmsGql') : null;
const hostname = `fe.${hostnameWithoutSubdomain}`;

const nodeEnv = config.has('nodeEnv') ? config.get('nodeEnv') : null;
const namespace = config.has('namespace') ? config.get('namespace') : null;

const FETCH_TIMEOUT = config.has('fetchTimeout') ? config.get('fetchTimeout') : 14000;

const directCMSClusterQueries = [
  'ArticleServerQuery',
  'SectionServerQuery',
  'AuthorServerQuery',
  'TagServerQuery',
  'ClusterServerQuery',
  'LiveBlogItemsServerQuery',
  'TenRecipesServerActionQuery',
  'GetCommentsServerActionQuery',
];

const directCMSQueries = [
  ...directCMSClusterQueries,
  'ImmutablePageDataServerQuery',
  'ImmutablePageElementsServerQuery',
  'SharedPageElementsServerQuery',
  'PurchasePagePersonalQuery',
  'LoginPageServerQuery',
];

const praedictaDirectQueries = [
  'ParagraphTickerServerQuery',
  'StockAssetsServerQuery',
  'IndexAssetsTableServerQuery',
];

const userInfoQueries = [
  'GetPersonalAreaServerQuery',
  'UserTypePersonalQuery',
  'GetUserByMailPersonalQuery',
];

const isLocal = nodeEnv?.startsWith('dev');
const isTaskNamespace = namespace?.startsWith('cu-') || false;

const queryMap = new Map<string, readonly [string, string | null, string | null]>();

interface GraphQLError {
  message?: string;
  /**
   * The response position that was nulled. Present on execution errors, absent on
   * request errors — see {@link resolvePayload}.
   */
  path?: (string | number)[];
}

interface GraphQLResponse<TData> {
  data?: TData | null;
  errors?: GraphQLError[];
}

/** The server has not seen this persisted query, so it must be POSTed in full. */
function isPersistedQueryMiss(error: GraphQLError): boolean {
  return error.message === 'PersistedQueryNotFound';
}

/**
 * Reads a GraphQL payload without letting a non-JSON body (an HTML 502 page
 * from the gateway, say) surface as an opaque `SyntaxError`.
 */
async function parseGraphQLResponse<TData>(
  response: Response
): Promise<GraphQLResponse<TData> | null> {
  try {
    return (await response.json()) as GraphQLResponse<TData>;
  } catch {
    return null;
  }
}

/**
 * Turns a GraphQL payload into data, or throws.
 *
 * The discriminator is the presence of `data`, **not** whether `errors` is empty.
 * Per spec §7 a *request error* result "must not contain an entry with key
 * `data`", and an error that prevented a valid response sets `data` to `null` —
 * in both cases nothing executed and there is nothing to salvage. An *execution*
 * (field) error is different: "a response may contain both a partial response as
 * well as a list of errors in the case that any execution error was raised and
 * replaced with `null`". Execution succeeded, only the errored positions are
 * `null`, and the rest of `data` is authoritative.
 *
 * This distinction is load-bearing rather than pedantic. Brightspot reports "no
 * page at this path" as HTTP 200 with a populated `errors` and
 * `data: { Page: null }` — a textbook partial response. Treating a non-empty
 * `errors` as a failure discards that valid `data`, and every 404 becomes a 500.
 *
 * The cost of getting this right is that a Brightspot *field-level* bug is
 * indistinguishable from a missing page and will 404. That is accepted: the
 * compensating control is `HTZ_GRAPHQL_PARTIAL`, alertable on rate. Request-level
 * and transport failures still throw, so a real outage stays a 500 — see
 * `requireQueryData` for why that matters.
 */
function resolvePayload<TData>(
  payload: GraphQLResponse<TData> | null,
  operationName: string,
  status: number
): TData {
  const [firstError] = payload?.errors ?? [];

  if (payload?.data == null) {
    // An unparseable or absent body on a non-2xx response is a transport failure,
    // not a legitimately empty result.
    const emptyDataReason = status >= 200 && status < 300 ? 'EMPTY_DATA' : 'HTTP_ERROR';

    throw new GraphqlFetchError(firstError?.message || `Empty data for ${operationName}`, {
      reason: firstError ? 'GRAPHQL_ERROR' : emptyDataReason,
      operation: operationName,
      status,
    });
  }

  if (firstError) {
    logQueryFailure({
      event: 'HTZ_GRAPHQL_PARTIAL',
      operation: operationName,
      // Not wrapped in an `Error`: this runs once per 404, and a stack capture per
      // missing page buys nothing.
      error: firstError,
      reason: 'GRAPHQL_ERROR',
      status,
      // A nulled `Page` is a missing page; a nulled `Page.slots.marketsBar` is a
      // broken widget on a page that rendered. The alert needs to tell them apart.
      path: firstError.path?.join('.'),
      // Routine 404 traffic, not a fault — see `logQueryFailure`.
      level: 'warn',
    });
  }

  return payload.data;
}

export default function fetcherFactory<TData, TVariables>(
  query: string | { toString(): string },
  variables?: TVariables,
  headers?: RequestInit['headers'],
  options: Pick<RequestInit, 'cache' | 'next'> = {}
): (context?: Pick<QueryFunctionContext, 'signal'>) => Promise<TData> {
  const queryStr = typeof query === 'string' ? query : String(query);

  return async function fetcher(context) {
    try {
      // Copy rather than rewrite in place: `options` belongs to the caller, and
      // data fetchers such as getSection build one `next` object and reuse it
      // across branches, so hashing in place writes back into their state.
      const requestOptions = options.next?.tags?.length
        ? {
            ...options,
            next: {
              ...options.next,
              tags: options.next.tags.map(tag =>
                tag && tag.length > 256 ? sha256(tag).toString(hexEnc) : tag
              ),
            },
          }
        : options;

      let [sha256Hash, operationName, operationType] = queryMap.get(queryStr) || ['', null, null];

      if (!sha256Hash) {
        const document = parseQuery(queryStr);

        sha256Hash = sha256(queryStr).toString(hexEnc);

        if (document) {
          operationName = getOperationName(document);
          operationType = getOperationType(document);
        }

        const parsedQuery = [sha256Hash, operationName, operationType] as const;

        queryMap.set(queryStr, parsedQuery);
      }

      operationName = operationName || 'unknown';
      operationType = operationType || 'unknown';

      const timeoutSignal =
        typeof window === 'undefined' ? timeoutSignalFactory(FETCH_TIMEOUT) : undefined;

      let url = gqlServerUrl.startsWith('https://')
        ? switchToDomain(
            gqlServerUrl as HtzWebsiteExternalURL<Env, 'gql'>,
            hostnameWithoutSubdomain
          )
        : gqlServerUrl;

      const isListQuery = operationName.endsWith('ListServerQuery');
      const { host, connection, ...defaultHeaders } = {
        ...(headers || {}),
        'content-type': 'application/json',
        accept: 'application/json',
        hostname,
      } as Record<string, string>;

      if ((directCMSQueries.includes(operationName) || isListQuery) && cmsGqlUrl) {
        let domain = cmsGqlUrl;

        if (typeof window !== 'undefined') {
          delete defaultHeaders.hostname;
          delete defaultHeaders['content-type'];
        }

        if (typeof window === 'undefined' && !isLocal && !isStorybook() && !isTaskNamespace) {
          if (
            (directCMSClusterQueries.includes(operationName) || isListQuery) &&
            gqlServerClusterUrl
          ) {
            domain = gqlServerClusterUrl as typeof domain;
          } else {
            // domain = cmsUri.replace('https:', 'http:') as typeof domain;
            domain = cmsGqlUrl as typeof domain;
          }
        }

        url = `${domain}` as unknown as typeof url;
      }

      if (
        typeof window === 'undefined' &&
        praedictaDirectQueries.includes(operationName) &&
        praedictaUrl
      ) {
        url = praedictaUrl as unknown as typeof url;
      }

      const isUserInfoQuery = userInfoQueries.includes(operationName) && userInfoUrl;

      if (isUserInfoQuery) {
        url = userInfoUrl as unknown as typeof url;
      }

      const { signal } = context || {};

      const params = {
        operationName,
        variables: JSON.stringify(variables || {}),
        extensions: JSON.stringify({
          persistedQuery: {
            version: 1,
            sha256Hash,
          },
        }),
      };

      const queryParams = new URLSearchParams(params);

      const queryParamsString = queryParams.toString();

      const fullUrl = `${url}?${queryParamsString}`;

      const isValidURL = fullUrl.length < 2000;

      const allQueryParamsIsValid = Array.from(queryParams.values()).every(
        value => value.length <= 256
      );

      if (operationType === 'query' && isValidURL && allQueryParamsIsValid && !isUserInfoQuery) {
        const persistedResult = await fetch(fullUrl, {
          credentials: 'include',
          method: 'GET',
          headers: defaultHeaders,
          signal: signal || timeoutSignal,
          ...requestOptions,
        });

        if (persistedResult.status < 400) {
          const persistedData = await parseGraphQLResponse<TData>(persistedResult);

          // A body we cannot parse is treated like a persisted-query miss so the
          // POST fallback still gets a turn, instead of failing the whole query.
          //
          // The miss test must stay ahead of `resolvePayload`: Brightspot answers a
          // miss with HTTP 200 and `data: null`, which `resolvePayload` would treat
          // as a request error and throw on — taking down every cold-cache query
          // rather than letting it fall through to the POST that resolves it.
          if (persistedData && !persistedData.errors?.some(isPersistedQueryMiss)) {
            // A request error here is not retried over POST: the same query would
            // earn the same error, and retrying doubles upstream load during the
            // outage that caused it.
            return resolvePayload(persistedData, operationName, persistedResult.status);
          }
        }
      }

      const result = await fetch(`${url}?operation=${operationName}`, {
        method: 'POST',
        credentials: 'include',
        headers: defaultHeaders,
        body: JSON.stringify({
          query: queryStr,
          variables: JSON.parse(queryParams.get('variables') || ''),
          operationName,
          extensions: JSON.parse(queryParams.get('extensions') || ''),
        }),
        signal, // || timeoutSignal // TODO:: Uncomment to activate timeout signal: ,
        ...requestOptions,
      });

      const json = await parseGraphQLResponse<TData>(result);

      return resolvePayload(json, operationName, result.status);
    } catch (error) {
      const document = parseQuery(queryStr);

      const operationName = (document && getOperationName(document)) || '';

      logQueryFailure({ event: 'HTZ_GRAPHQL_FETCH_FAILED', operation: operationName, error });

      throw error;
    }
  };
}
